import { filterUsageProducts, isNotMeteredCreditProductOnly, type PerUnitProduct } from '@vy/services';
import type { CatalogProducts, Currency, FixedProduct, Plan, UsageProduct } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import React, { useMemo } from 'react';
import { useTreeData } from '../../../../hooks/use-tree-data';
import { ContractExpandableSection } from '../ContractExpandableSection/ContractExpandableSection';
import type { OnEditAnrokHandler } from '../shared/anrok-cell-types';
import { ContractUsageProductsTable } from './components/ContractUsageProductsTable';
import { getUsageProductsColumns } from './contract-usage-products-constants/UsageProductsColumns';
import type { UsageProductsColumns } from './contract-usage-products-types/usage-products-columns';
import { buildRowsFromUsageProducts } from './contract-usage-products-utils/usage-product-rows-builder';

type ContractUsageProductsSectionProps = {
  plan: Plan;
  currency: Currency;
  isAnrokEnabled?: boolean;
  isConnactEnabled?: boolean;
  catalogProductsById?: Map<string, CatalogProducts>;
  onEditErp?: (product: FixedProduct | PerUnitProduct | UsageProduct, productName: string) => void;
  onEditAnrok?: OnEditAnrokHandler;
};

export const ContractUsageProductsSection: React.FC<ContractUsageProductsSectionProps> = ({
  plan,
  currency,
  isAnrokEnabled = false,
  isConnactEnabled = false,
  catalogProductsById,
  onEditErp,
  onEditAnrok,
}) => {
  const usageProducts = useMemo(() => filterUsageProducts<UsageProduct>(plan.products), [plan]);
  const usageProductsWithoutMeteredCredits = useMemo(
    () => usageProducts.filter(isNotMeteredCreditProductOnly),
    [usageProducts],
  );

  const initialRows = useMemo(
    () => buildRowsFromUsageProducts(usageProductsWithoutMeteredCredits, catalogProductsById),
    [usageProductsWithoutMeteredCredits, catalogProductsById],
  );

  const rows = useTreeData<UsageProductsColumns>(initialRows);
  const columns = getUsageProductsColumns(currency, { isAnrokEnabled, isConnactEnabled });

  const isParentRow = (row: UsageProductsColumns) => !row.isChild;
  const parentRows = useMemo(() => rows.filter(isParentRow), [rows]);

  if (isEmptyArray(rows)) {
    return null;
  }

  return (
    <ContractExpandableSection sx={{ padding: '0px 6px' }} title="Usage products" count={parentRows.length}>
      <ContractUsageProductsTable
        rows={rows}
        columns={columns}
        currency={currency}
        onEditErp={onEditErp}
        onEditAnrok={onEditAnrok}
      />
    </ContractExpandableSection>
  );
};

